import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import {useSearchParams} from 'react-router-dom';

import {ICheckoutResultResponseModel} from '@/dataSource/src/api/payment-link/entities';
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';
import {usePaymentLinkRepository} from '@/dataSource/src/api/payment-link/useRepository';

interface ViewModelContextType {
  checkoutResultData: ICheckoutResultResponseModel | undefined;
  handleGetCheckoutResult: (checkoutId?: string) => void;
  showModalPending: boolean;
  setShowModalPending: React.Dispatch<React.SetStateAction<boolean>>;
  isSinglePayment?: boolean;
}

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  const [searchParams] = useSearchParams();
  const [showModalPending, setShowModalPending] = useState(false);

  const checkoutId = searchParams.get('id');
  const isSinglePayment = searchParams.get('single_payment') === 'true';
  const {checkoutResultData, handleGetCheckoutResult} =
    usePaymentLinkRepository(checkoutId);

  const {trackViewPaymentOutcomeSuccess} = usePaymentLinkTrackers();

  useEffect(() => {
    handleGetCheckoutResult(checkoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutId]);

  useEffect(() => {
    if (checkoutResultData) {
      if (checkoutResultData.status === 'pending') {
        setShowModalPending(true);
        setTimeout(() => {
          handleGetCheckoutResult(checkoutId);
        }, 5000);
      } else {
        trackViewPaymentOutcomeSuccess(checkoutId, checkoutResultData);
        setShowModalPending(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutId, checkoutResultData]);

  return (
    <ViewModelContext.Provider
      value={{
        checkoutResultData,
        handleGetCheckoutResult,
        showModalPending,
        setShowModalPending,
        isSinglePayment,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export const useViewModelProvider = () => {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error(
      'usePaymentLinkOutcomeContext must be used within PaymentLinkContext',
    );
  }
  return context;
};
